import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ReloadOutlined, FilePdfOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiErrorMessage } from '@/api/client';
import { listPayees, type Payee } from '@/api/payees';
import {
  listPayouts,
  refreshPayout,
  checkAllPayouts,
  type Payout,
  type PayoutFilter,
  type PayoutStatus,
} from '@/api/payouts';
import {
  PAYOUT_STATUS_LABEL,
  payoutStatusView,
  RECEIPT_PLACEHOLDER,
  RECEIPT_PLACEHOLDER_HINT,
  feeLine,
  isTerminalPayout,
} from '@/domain/payouts';
import { useSessionStore } from '@/store/session';
import AmountWithCNY from '@/components/common/AmountWithCNY';
import { ReceiptPreviewModal } from '@/components/common/ReceiptPreviewModal';
import AmountRangeFilter from '@/components/common/AmountRangeFilter';
import FilterBar from '@/components/common/FilterBar';
import FailureDetailView from '@/components/payouts/FailureDetailView';
import PayoutDetailDrawer from '@/components/payouts/PayoutDetailDrawer';
import { detailFromPayout } from '@/domain/payoutDetail';
import { useListFilter } from '@/hooks/useListFilter';

const { Text } = Typography;

/**
 * 整行可点之后，行内那些**自己有事干**的元素必须把点击拦在自己这里，
 * 否则点「查看确认函」会顺带把抽屉也弹出来，两层东西叠在一起。
 */
const stop = (e: ReactMouseEvent) => e.stopPropagation();

const STATUS_OPTIONS = (Object.keys(PAYOUT_STATUS_LABEL) as PayoutStatus[]).map((value) => ({
  value,
  label: PAYOUT_STATUS_LABEL[value],
}));

const RECEIPT_OPTIONS = [
  { value: true, label: '已有确认函' },
  // 不写「暂无」：这一档里混着「还没取到」和「永远不会有」（上游拒收），
  // 一个「暂」字会让后者看起来只是还没到
  { value: false, label: '无确认函' },
];

/**
 * 打款记录：每一行都是一次**真实发生过**的资金操作。
 *
 * 这一页是只读的（除了刷新状态与下载转账确认函）—— 记录本身由系统写入，不接受人工修改。
 */
export default function Payouts() {
  const { message } = App.useApp();
  const me = useSessionStore((s) => s.me);

  const [rows, setRows] = useState<Payout[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  /**
   * 🔴 未出最终结果的笔数，**来自服务端的全量统计**，不是 `rows` 里数出来的。
   * 从筛过的列表里数，财务勾一下「已到账」就会看到提示消失 —— 而那几笔状态未定的钱
   * 一分都没少。在这个系统里「看不见」和「不存在」绝不能长得一样。
   */
  const [pendingCount, setPendingCount] = useState(0);
  /**
   * 🔴 到账后又被退回的笔数，同样来自服务端全量统计。
   *
   * 这条提示是这类事件在界面上**唯一的主动出口**：退回可能发生在到账后 37 小时到
   * 58 天，那时没有人还在盯那条几十天前的记录；而它已是终态，不计入 pendingCount。
   * 没有它，「钱退回来了」在界面上等于没发生过 —— 只有正好翻到那一行才看得见。
   */
  const [returnedCount, setReturnedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  /** 正在查看上游原文的那条打款记录。null = 弹窗关着。 */
  const [failureOf, setFailureOf] = useState<Payout | null>(null);
  /** 正在预览转账确认函的那条打款记录。null = 弹窗关着。 */
  const [previewOf, setPreviewOf] = useState<Payout | null>(null);
  /** 正在看哪一条打款记录的详情。null = 抽屉关着。 */
  const [detailRow, setDetailRow] = useState<Payout | null>(null);
  /** 抽屉里那一段收款人信息。跟着 detailRow 异步拉，拉不到就在抽屉里明说。 */
  const [detailPayee, setDetailPayee] = useState<Payee | null>(null);
  const [detailPayeeLoading, setDetailPayeeLoading] = useState(false);
  const [detailPayeeError, setDetailPayeeError] = useState<string | null>(null);
  /** 收款人列表拉过一次就复用 —— 抽屉里的收款信息取本地镜像，与收款人页同源。 */
  const payeeCache = useRef<Payee[] | null>(null);
  /** 连着快点两行时只认最后点的那一条，先发的那次回来不再写状态。 */
  const detailSeq = useRef(0);
  const [checkingAll, setCheckingAll] = useState(false);
  const f = useListFilter<PayoutFilter>();
  const { setMany } = f;
  /** 「清空筛选」时连带把金额输入框也抹掉 —— 它自己存着一份本地值。 */
  const [resetKey, setResetKey] = useState(0);
  /** 能不能手动核对。核对是只读操作、不动钱，所以比打款宽一档：管理员也可以。 */
  const canCheck = (me?.roles.some((r) => r === 'finance' || r === 'admin') ?? false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listPayouts(f.filter);
      setRows(data.items);
      setCurrencies(data.facets.currencies);
      setPendingCount(data.pendingCount);
      setReturnedCount(data.returnedCount);
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message, f.filter]);

  function resetFilter() {
    f.reset();
    setResetKey((k) => k + 1);
  }

  /** 金额区间：🔴 两端都是字符串，原样交给接口，全程不进 Number。 */
  const onAmountRange = useCallback(
    (amountMin?: string, amountMax?: string) => setMany({ amountMin, amountMax }),
    [setMany],
  );

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect
    void load();
  }, [load]);

  async function checkAll() {
    setCheckingAll(true);
    try {
      const { checked } = await checkAllPayouts();
      message.success(checked > 0 ? `已核对 ${checked} 笔` : '没有需要核对的打款');
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setCheckingAll(false);
    }
  }

  /**
   * 打开这条打款记录的详情。
   *
   * 抽屉**立刻**打开：这一行的信息（金额、状态、单号、费用、确认函，以及它依据的那条
   * 申请的附言 / 备注 / 申请人）已经在手上了。收款人那一段单独异步拉，取不到就在抽屉里
   * 明说「没取到」——留白会让人以为这个收款人压根没填收款信息。
   *
   * 🔴 收款信息取 `/payees/list` 的本地镜像，不走 `/payees/get`：后者会拿远端结果覆盖
   * 镜像，远端回空时连账户名和币种一起清掉（见 payees.service.ts 的 getPayee）。
   * 一个只读的详情抽屉不该有能力改坏数据。
   */
  function openDetail(row: Payout) {
    const seq = ++detailSeq.current;
    setDetailRow(row);
    setDetailPayee(null);
    setDetailPayeeError(null);
    setDetailPayeeLoading(true);
    void (async () => {
      try {
        const list = payeeCache.current ?? (await listPayees()).items;
        payeeCache.current = list;
        if (seq !== detailSeq.current) return;
        const found = list.find((x) => x.id === row.payeeId);
        if (!found) {
          setDetailPayeeError('找不到这个收款人，请刷新后重试');
          return;
        }
        setDetailPayee(found);
      } catch (err) {
        if (seq !== detailSeq.current) return;
        setDetailPayeeError(apiErrorMessage(err));
      } finally {
        if (seq === detailSeq.current) setDetailPayeeLoading(false);
      }
    })();
  }

  function closeDetail() {
    // 推进序号：还在路上的那次收款人请求回来后不再写状态
    detailSeq.current += 1;
    setDetailRow(null);
    setDetailPayee(null);
    setDetailPayeeLoading(false);
  }

  async function refresh(row: Payout) {
    setRefreshing(row.id);
    try {
      await refreshPayout(row.id);
      message.success('已向 Airwallex 核对状态');
      void load();
    } catch (err) {
      message.error(apiErrorMessage(err));
    } finally {
      setRefreshing(null);
    }
  }


  return (
    <Card
      title="打款记录"
      extra={
        <Space>
          {canCheck && (
            <Tooltip title="立即向 Airwallex 核对所有未完成的打款">
              <Button
                icon={<SyncOutlined />}
                loading={checkingAll}
                disabled={pendingCount === 0}
                onClick={checkAll}
              >
                检查状态
              </Button>
            </Tooltip>
          )}
          <Tooltip title="重新加载列表">
            <Button icon={<ReloadOutlined />} onClick={() => void load()} />
          </Tooltip>
        </Space>
      }
    >
      {/* 🔴 这条提示按**全量**统计，筛选不影响它：被筛掉的未完成打款同样是还没落地的钱 */}
      {pendingCount > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="info"
          showIcon
          title={`有 ${pendingCount} 笔打款尚未出最终结果`}
          description="系统每小时整点自动核对一次；需要立刻知道结果就点右上角「检查状态」。请勿因为状态未定就重新发起打款。"
        />
      )}

      {/* 🔴 同样按全量统计。钱已经退回来了，筛选看不看得见它都不改变这件事 */}
      {returnedCount > 0 && (
        <Alert
          style={{ marginBottom: 16 }}
          type="error"
          showIcon
          title={`有 ${returnedCount} 笔打款到账后被退回`}
          description={
            <Space direction="vertical" size={4}>
              <span>
                这些钱曾经打成功、之后被收款行退票或收款人要求退回，已退回 Airwallex 账户。
                如果当时已按到账处理过后续（通知对方、记账），需要回头处理。
              </span>
              <Button
                size="small"
                danger
                onClick={() => setMany({ returned: true })}
              >
                只看这些
              </Button>
            </Space>
          }
        />
      )}

      <FilterBar active={f.active} resultCount={rows.length} onReset={resetFilter}>
        <Input
          allowClear
          style={{ width: 220 }}
          placeholder="搜收款人 / 短 id / Airwallex 单号"
          value={f.keyword}
          onChange={(e) => f.setKeyword(e.target.value)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 180 }}
          placeholder="状态"
          options={STATUS_OPTIONS}
          value={f.filter.status ?? []}
          onChange={(v: PayoutStatus[]) => f.set('status', v)}
        />
        <Select
          mode="multiple"
          allowClear
          style={{ minWidth: 120 }}
          placeholder="币种"
          options={currencies.map((c) => ({ value: c, label: c }))}
          value={f.filter.currency ?? []}
          onChange={(v: string[]) => f.set('currency', v)}
        />
        <AmountRangeFilter onChange={onAmountRange} resetKey={resetKey} />
        <Select
          allowClear
          style={{ minWidth: 140 }}
          placeholder="转账确认函"
          options={RECEIPT_OPTIONS}
          value={f.filter.hasReceipt}
          onChange={(v?: boolean) => f.set('hasReceipt', v)}
        />
        <DatePicker.RangePicker
          placeholder={['打款起', '打款止']}
          value={f.range}
          onChange={f.setRange}
        />
      </FilterBar>

      <Table<Payout>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        /*
         * 整行可点 —— 这一行里**没有任何会动钱的按钮**（「刷新状态」只是向 Airwallex
         * 问一句，不动钱），所以可以像收款人页那样整行打开详情。
         * 🔴 打款申请页不能这么做：那一行最右边是不可撤销的「打款」，PRODUCT.md §4.6
         * 明确要求它的点击区域不和别的东西重叠，所以那边只有收款人姓名可点。
         */
        onRow={(row) => ({ onClick: () => openDetail(row), style: { cursor: 'pointer' } })}
        columns={[
          {
            // 短 id 并进这一列而不是单开一列，理由同打款申请页：它说的就是「这是谁」
            title: '收款人',
            width: 170,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Text strong>{row.payeeName ?? '—'}</Text>
                {/* 不许断行：短 id 被折成两行就没法一眼抄下来了 */}
                <Text code style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                  {row.payeeShortId}
                </Text>
              </Space>
            ),
          },
          {
            title: '金额',
            width: 170,
            align: 'right',
            // 手续费并进这一列，不新开一列 —— 这张表已经横向滚动了
            render: (_, row) => {
              const fee = feeLine(row.fee, row.status);
              return (
                <div style={{ textAlign: 'right' }}>
                  <AmountWithCNY amount={row.amount} currency={row.currency} />
                  {fee && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {fee}
                    </Text>
                  )}
                </div>
              );
            },
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 170,
            render: (s: Payout['status'], row) => (
              <Space direction="vertical" size={0}>
                {(() => {
                  // 「已到账后退回」与「已取消」在这里分开说，判据在 domain/payouts.ts
                  const v = payoutStatusView(row);
                  return <Tag color={v.color}>{v.label}</Tag>;
                })()}
                {row.providerStatus && row.providerStatus !== s && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    Airwallex: {row.providerStatus}
                  </Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Airwallex 单号',
            dataIndex: 'shortReferenceId',
            width: 150,
            render: (v: string | null) =>
              v ? (
                // 复制图标自己处理点击，别顺手把抽屉也打开
                <span onClick={stop}>
                  <Text code copyable style={{ fontSize: 12 }}>
                    {v}
                  </Text>
                </span>
              ) : (
                '—'
              ),
          },
          {
            title: '失败原因',
            dataIndex: 'failureReason',
            width: 220,
            render: (v: string | null, row) =>
              v ? (
                <Space direction="vertical" size={0}>
                  <Tooltip title={v}>
                    <Text type="danger" style={{ fontSize: 12 }} ellipsis>
                      {v}
                    </Text>
                  </Tooltip>
                  {/* 上游原文给研发看：省得为一句「打款失败」去翻服务器日志 */}
                  {row.failureDetail && (
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingInline: 0, height: 20 }}
                      onClick={(e) => {
                        stop(e);
                        setFailureOf(row);
                      }}
                    >
                      查看上游原文
                    </Button>
                  )}
                </Space>
              ) : (
                '—'
              ),
          },
          { title: '操作人', dataIndex: 'executedByName', width: 90 },
          {
            title: '时间',
            dataIndex: 'createdAt',
            width: 160,
            render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
          {
            /*
             * 🔴 叫「转账确认函」而不是「回单」：它证明的是 Airwallex **受理了这笔
             * 转账指令**，不是「钱已经到对方账上」。两者在非终态时会同时存在，
             * 而「有回单」很容易被读成「打完了」—— 在资金界面上这个误读是要出事的。
             * 所以未到终态时，紧挨着按钮写明它不代表到账，以状态列为准。
             */
            title: '转账确认函',
            width: 150,
            render: (_, row) =>
              row.receipt === 'ARCHIVED' ? (
                <Space direction="vertical" size={0}>
                  <Button
                    type="link"
                    size="small"
                    style={{ paddingInline: 0 }}
                    icon={<FilePdfOutlined />}
                    onClick={(e) => {
                      stop(e);
                      setPreviewOf(row);
                    }}
                  >
                    查看
                  </Button>
                  {!isTerminalPayout(row.status) && (
                    <Tooltip title="确认函说明 Airwallex 已受理这笔转账指令，钱是否到账以「状态」列为准。">
                      <Text type="warning" style={{ fontSize: 11 }}>
                        不代表已到账
                      </Text>
                    </Tooltip>
                  )}
                </Space>
              ) : (
                <Tooltip title={RECEIPT_PLACEHOLDER_HINT[row.receipt]}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {RECEIPT_PLACEHOLDER[row.receipt]}
                  </Text>
                </Tooltip>
              ),
          },
          ...(canCheck
            ? [
                {
                  title: '',
                  width: 60,
                  render: (_: unknown, row: Payout) =>
                    isTerminalPayout(row.status) ? null : (
                      <Tooltip title="向 Airwallex 核对状态">
                        <Button
                          type="text"
                          size="small"
                          // 图标按钮没有文字，不给 aria-label 的话读屏和测试都点不到它
                          aria-label="向 Airwallex 核对状态"
                          icon={<ReloadOutlined />}
                          loading={refreshing === row.id}
                          onClick={(e) => {
                            stop(e);
                            void refresh(row);
                          }}
                        />
                      </Tooltip>
                    ),
                },
              ]
            : []),
        ]}
      />

      {/*
        上游原文。**给研发看的**，所以不翻译、不美化，原样展示 Airwallex 回的状态码、
        错误码与出错字段路径，并给一个复制按钮 —— 排查时直接贴进 issue。
        🔴 这里只有后端白名单挑过的字段（payouts.service.ts 的 applyRejection），
        不含任何账号值。
      */}
      <Modal
        open={failureOf !== null}
        onCancel={() => setFailureOf(null)}
        title="上游返回的失败原文"
        footer={null}
        width={620}
      >
        {failureOf?.failureDetail && (
          <FailureDetailView
            detail={failureOf.failureDetail}
            payoutId={failureOf.id}
            shortReferenceId={failureOf.shortReferenceId}
          />
        )}
      </Modal>

      {/*
        与打款申请页共用同一个抽屉：一笔钱的申请侧与执行侧，在哪一页点开看到的都是同一份。
        🔴 只读 —— 抽屉里不放任何动钱的按钮。
      */}
      <PayoutDetailDrawer
        detail={detailRow ? detailFromPayout(detailRow) : null}
        payee={detailPayee}
        payeeLoading={detailPayeeLoading}
        payeeError={detailPayeeError}
        onClose={closeDetail}
        onViewReceipt={() => setPreviewOf(detailRow)}
      />

      {/* 转账确认函的页内预览。字节由后端代出来，理由见组件顶部说明 */}
      <ReceiptPreviewModal
        /* 🔴 key 让每次打开都是一次全新挂载：组件内的 loading / url / error 初值天然正确，
           不必在 effect 里同步 setState 把它们重置一遍。 */
        key={previewOf?.id ?? 'none'}
        payoutId={previewOf?.id ?? null}
        subtitle={
          previewOf ? `${previewOf.payeeName ?? "—"} · ${previewOf.amount} ${previewOf.currency}` : undefined
        }
        onClose={() => setPreviewOf(null)}
      />
    </Card>
  );
}
